import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { effectiveType, isNumericType, isDateType, defaultAggFor, TYPE_OPTIONS, AGG_CHOICES, typeIcon } from "@/lib/fieldWells";
import { X, Loader2, Sigma, Copy, FunctionSquare, Wrench, BarChart3 } from "lucide-react";

const DATA_CATEGORIES = ["none", "country", "state", "city", "postal_code", "latitude", "longitude", "address", "image_url", "web_url"];
const FMT_TYPES = [{ v: "none", l: "Automatic" }, { v: "number", l: "Number" }, { v: "currency", l: "Currency" }, { v: "percent", l: "Percent" }];

const nfmt = (v) => (v == null ? "—" : typeof v === "number" ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v));

// Deep single-field detail: metadata editing + profile stats + value distribution + actions.
export function FieldProfilePanel({ ds, field, model, onModelChange, onClose, onDatasetsChanged }) {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const schema = ds?.schema || [];
  const fm = model?.field_meta || {};
  const meta = fm[field] || {};
  const eff = effectiveType(field, schema, fm);
  const numeric = isNumericType(eff);
  const agg = defaultAggFor(field, schema, fm);
  const isCalc = useMemo(() => (ds?.calc_fields || []).some((f) => f.name === field), [ds, field]);

  useEffect(() => {
    if (!ds?.id || !field) return;
    setProfile(null); setLoading(true);
    api.get(`/datasets/${ds.id}/field-profile`, { params: { field } })
      .then(({ data }) => setProfile(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to profile field"))
      .finally(() => setLoading(false));
  }, [ds?.id, field]);

  const saveMeta = async (patch) => {
    const next = { ...(model.field_meta || {}) };
    next[field] = { ...(next[field] || {}), ...patch };
    Object.keys(next[field]).forEach((k) => { if (next[field][k] == null || next[field][k] === "" || next[field][k] === "none") delete next[field][k]; });
    if (!Object.keys(next[field]).length) delete next[field];
    try {
      const { data } = await api.put(`/datasets/${ds.id}/model`, { ...stripModel(model), field_meta: next });
      onModelChange(data);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only the dataset owner can edit field settings" : (e?.response?.data?.detail || "Failed to save"));
    }
  };

  const createMeasure = async () => {
    const a = numeric && agg !== "none" ? agg : "count";
    const key = `${a}_${field}`.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
    if ((model.measures || []).some((m) => m.key === key)) { toast.info("A measure for this field & aggregation already exists"); return; }
    try {
      const { data } = await api.put(`/datasets/${ds.id}/model`, {
        ...stripModel(model),
        measures: [...(model.measures || []), { key, label: `${a.replace(/_/g, " ")} of ${meta.display_name || field}`, field, agg: a, kind: "simple", visible: true }],
      });
      onModelChange(data);
      toast.success("Measure created — it's now in the data pane");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create measure"); }
  };

  const duplicateColumn = async () => {
    const name = `${field}_copy`;
    if (schema.some((c) => c.name === name) || (ds.calc_fields || []).some((f) => f.name === name)) { toast.info(`${name} already exists`); return; }
    try {
      await api.put(`/datasets/${ds.id}/calc-fields`, { fields: [...(ds.calc_fields || []).map(({ name: n, expression }) => ({ name: n, expression })), { name, expression: `[${field}]` }] });
      toast.success(`Created calculated column ${name}`);
      onDatasetsChanged && onDatasetsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to duplicate column"); }
  };

  const Icon = typeIcon(eff);
  const maxCount = Math.max(1, ...((profile?.top_values || []).map((t) => t.count)));

  return (
    <div data-testid="field-profile-panel" className="w-[340px] shrink-0 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] flex flex-col min-h-0">
      <div className="flex items-start justify-between gap-2 p-3 border-b border-[color:var(--glass-border)]">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[color:var(--ink)] flex items-center gap-1.5 truncate"><Icon className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />{meta.display_name || field}{isCalc ? <FunctionSquare className="w-3 h-3 text-[color:var(--brand-mint)]" /> : null}</p>
          <p className="text-[10px] font-mono text-[color:var(--ink-3)] truncate">{field} · {numeric && agg !== "none" ? "Measure" : "Dimension"}</p>
        </div>
        <button data-testid="field-profile-close" onClick={onClose} className="p-1 rounded-md text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]"><X className="w-3.5 h-3.5" /></button>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Display name</Label>
            <Input data-testid="field-profile-rename" defaultValue={meta.display_name || ""} placeholder={field}
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (meta.display_name || "")) saveMeta({ display_name: v || null }); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="h-8 text-xs bg-[color:var(--surface-2)]" />
            <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Description</Label>
            <Input data-testid="field-profile-description" defaultValue={meta.description || ""} placeholder="What does this field mean?"
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (meta.description || "")) saveMeta({ description: v || null }); }}
              className="h-8 text-xs bg-[color:var(--surface-2)]" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Data type</Label>
              <Select value={eff} onValueChange={(t) => saveMeta({ type_override: t === (schema.find((c) => c.name === field)?.type || "text") ? null : t })}>
                <SelectTrigger data-testid="field-profile-type" className="h-8 text-xs bg-[color:var(--surface-2)] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Default aggregation</Label>
              <Select value={agg} onValueChange={(a) => saveMeta({ default_agg: a })}>
                <SelectTrigger data-testid="field-profile-agg" className="h-8 text-xs bg-[color:var(--surface-2)] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AGG_CHOICES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  {numeric && <SelectItem value="none">Do Not Summarize</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Data category</Label>
              <Select value={meta.data_category || "none"} onValueChange={(v) => saveMeta({ data_category: v })}>
                <SelectTrigger data-testid="field-profile-category" className="h-8 text-xs bg-[color:var(--surface-2)] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DATA_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "none" ? "Uncategorized" : c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Format</Label>
              <Select value={meta.format?.type || "none"} onValueChange={(v) => saveMeta({ format: v === "none" ? null : { type: v, decimals: meta.format?.decimals ?? (v === "percent" ? 1 : 0), compact: false } })}>
                <SelectTrigger data-testid="field-profile-format" className="h-8 text-xs bg-[color:var(--surface-2)] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{FMT_TYPES.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)]">
            Hidden from authors
            <Switch data-testid="field-profile-hidden" checked={!!meta.hidden} onCheckedChange={(v) => saveMeta({ hidden: v || null })} />
          </label>

          <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] mb-1.5">Profile{profile?.sampled ? " · 100k sample" : ""}</p>
            {loading ? <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-[color:var(--brand-ocean)]" /></div> : profile ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid="field-profile-stats">
                <Stat k="Total records" v={nfmt(profile.total_rows)} />
                <Stat k="Distinct" v={nfmt(profile.distinct_count)} />
                <Stat k="Nulls" v={`${nfmt(profile.null_count)} (${profile.null_pct}%)`} />
                <Stat k="Duplicates" v={nfmt(profile.duplicate_count)} />
                {profile.numeric && <>
                  <Stat k="Min" v={nfmt(profile.numeric.min)} />
                  <Stat k="Max" v={nfmt(profile.numeric.max)} />
                  <Stat k="Average" v={nfmt(profile.numeric.mean)} />
                  <Stat k="Median" v={nfmt(profile.numeric.median)} />
                  <Stat k="Sum" v={nfmt(profile.numeric.sum)} />
                </>}
                {profile.dates && <>
                  <Stat k="Earliest" v={String(profile.dates.earliest || "—").slice(0, 10)} />
                  <Stat k="Latest" v={String(profile.dates.latest || "—").slice(0, 10)} />
                </>}
              </div>
            ) : <p className="text-[11px] text-[color:var(--ink-3)]">No profile available.</p>}
          </div>

          {profile?.top_values?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] mb-1.5 flex items-center gap-1"><BarChart3 className="w-3 h-3" />Value distribution (top {profile.top_values.length})</p>
              <div className="space-y-1" data-testid="field-profile-distribution">
                {profile.top_values.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="w-[110px] truncate text-[color:var(--ink-2)]" title={String(t.value)}>{String(t.value ?? "—")}</span>
                    <div className="flex-1 h-3 rounded bg-[color:var(--surface-2)] overflow-hidden">
                      <div className="h-full rounded bg-[color:var(--brand-ocean)]/70" style={{ width: `${Math.max(3, (t.count / maxCount) * 100)}%` }} />
                    </div>
                    <span className="w-12 text-right tabular-nums text-[color:var(--ink-3)]">{nfmt(t.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5 pt-1 border-t border-[color:var(--glass-border)]">
            <p className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">Actions</p>
            <div className="grid grid-cols-2 gap-1.5">
              <Button data-testid="field-action-create-measure" size="sm" variant="outline" className="h-7 text-[11px] bg-[color:var(--surface-2)]" onClick={createMeasure}><Sigma className="w-3 h-3 mr-1" />Create measure</Button>
              <Button data-testid="field-action-duplicate" size="sm" variant="outline" className="h-7 text-[11px] bg-[color:var(--surface-2)]" onClick={duplicateColumn}><Copy className="w-3 h-3 mr-1" />Duplicate column</Button>
              <Button data-testid="field-action-calc-column" size="sm" variant="outline" className="h-7 text-[11px] bg-[color:var(--surface-2)]" onClick={() => onDatasetsChanged && onDatasetsChanged({ openCalc: true })}><FunctionSquare className="w-3 h-3 mr-1" />Calculated column</Button>
              <Button data-testid="field-action-open-prep" size="sm" variant="outline" className="h-7 text-[11px] bg-[color:var(--surface-2)]" onClick={() => nav(`/prep/${ds.id}`)}><Wrench className="w-3 h-3 mr-1" />Open in Prep</Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <>
      <span className="text-[color:var(--ink-3)]">{k}</span>
      <span className="text-right tabular-nums text-[color:var(--ink)] truncate" title={String(v)}>{v}</span>
    </>
  );
}

const stripModel = (data) => ({
  name: data.name || "Model", description: data.description || "", time_dimension: data.time_dimension || null,
  dimensions: data.dimensions || [], measures: data.measures || [], hierarchies: data.hierarchies || [],
  rls_rules: data.rls_rules || [], relationships: data.relationships || [], field_meta: data.field_meta || {},
});
